<template>
  <div class="tw-relative">
    <!-- Main Video Call Button -->
    <button
      @click="toggleDropdown"
      :disabled="loading || disabled"
      :class="[
        'tw-p-2 tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors tw-relative',
        { 'tw-opacity-50 tw-cursor-not-allowed': disabled || loading }
      ]"
      :title="loading ? 'Connecting...' : 'Start video call'"
    >
      <icon 
        v-if="loading" 
        icon="line-md:loading-twotone-loop" 
        class="tw-text-lg" 
      />
      <icon 
        v-else 
        icon="solar:videocamera-bold-duotone" 
        class="tw-text-lg" 
      />
    </button>

    <!-- Call Options Dropdown -->
    <div
      v-if="showDropdown"
      class="tw-absolute tw-top-full tw-right-0 tw-mt-2 tw-w-48 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-z-50"
      @click.stop
    >
      <div class="tw-p-2">
        <!-- Video Call Option -->
        <button
          @click="initiateCall('video')"
          :disabled="loading"
          class="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-text-left tw-text-gray-700 hover:tw-bg-gray-50 tw-rounded-lg tw-transition-colors"
        >
          <icon icon="solar:videocamera-bold-duotone" class="tw-text-lg tw-text-blue-500" />
          <div>
            <div class="tw-font-medium">Video Call</div>
            <div class="tw-text-xs tw-text-gray-500">Start a video call</div>
          </div>
        </button>

        <!-- Voice Call Option -->
        <button
        v-if="false"
          @click="initiateCall('voice')"
          :disabled="loading"
          class="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-text-left tw-text-gray-700 hover:tw-bg-gray-50 tw-rounded-lg tw-transition-colors"
        >
          <icon icon="solar:phone-calling-bold-duotone" class="tw-text-lg tw-text-green-500" />
          <div>
            <div class="tw-font-medium">Voice Call</div>
            <div class="tw-text-xs tw-text-gray-500">Start an audio-only call</div>
          </div>
        </button>
      </div>
    </div>

    <!-- Backdrop to close dropdown -->
    <div
      v-if="showDropdown"
      class="tw-fixed tw-inset-0 tw-z-40"
      @click="closeDropdown"
    ></div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, onUnmounted } from 'vue';
import videoCallsApi from '@/api/video-calls';
import messagingApi from '@/api/messages';
import useAlert from '@/composables/useAlert';

const props = defineProps({
  conversationId: {
    type: String,
    required: true
  },
  otherParticipant: {
    type: Object,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['call-initiated', 'error']);

// Reactive data
const loading = ref(false);
const showDropdown = ref(false);

// Import alert function (call directly)
// const alert = useAlert(); // Not needed, call useAlert() directly

/**
 * Toggle the call options dropdown
 */
const toggleDropdown = () => {
  if (!loading.value && !props.disabled) {
    showDropdown.value = !showDropdown.value;
  }
};

/**
 * Close the dropdown
 */
const closeDropdown = () => {
  showDropdown.value = false;
};

/**
 * Initiate a video or voice call
 * @param {string} callType - 'video' or 'voice'
 */
const initiateCall = async (callType) => {
  if (loading.value) return;
  
  // Log call initiation attempt
  console.log('📞 CALL INITIATION ATTEMPT:', {
    timestamp: new Date().toISOString(),
    callType: callType,
    conversationId: props.conversationId,
    otherParticipant: props.otherParticipant
  });
  
  try {
    loading.value = true;
    closeDropdown();
    
    // Step 1: Create room via backend
    const roomResponse = await videoCallsApi.rooms.create({
      conversation_id: props.conversationId,
      call_type: callType
    });
    
    if (!roomResponse.data?.success) {
      throw new Error('Failed to create call room');
    }
    
    const roomData = roomResponse.data.data;
    
    // Log successful room creation
    console.log('🏠 ROOM CREATED SUCCESSFULLY:', {
      timestamp: new Date().toISOString(),
      roomData: roomData,
      callType: callType,
      conversationId: props.conversationId
    });
    
    // Log URL field usage
    console.log('🔗 ROOM URL FIELD USAGE:', {
      timestamp: new Date().toISOString(),
      participant_room_url: roomData.participant_room_url,
      room_url: roomData.room_url,
      final_room_url: roomData.participant_room_url || roomData.room_url
    });
    
    // Step 2: Send call request message
    const messageData = {
      metadata: {
        room_id: roomData.room_id,
        call_type: callType,
        expires_at: new Date(Date.now() + 60000).toISOString(), // 1 minute to answer
        call_id: roomData.call_id // Include call_id if available
      }
    };
    
    const messageResponse = callType === 'video' 
      ? await messagingApi.messages.sendVideoCallRequest(props.conversationId, messageData)
      : await messagingApi.messages.sendVoiceCallRequest(props.conversationId, messageData);
    
    if (!messageResponse.data?.success) {
      throw new Error('Failed to send call request');
    }
    
    // Log successful call request message
    console.log('💬 CALL REQUEST MESSAGE SENT:', {
      timestamp: new Date().toISOString(),
      messageId: messageResponse.data.data.id,
      messageData: messageData,
      callType: callType,
      conversationId: props.conversationId
    });
    
    // Step 3: Emit success event with call_id
    emit('call-initiated', {
      roomId: roomData.room_id,
      roomUrl: roomData.participant_room_url || roomData.room_url, // Use new participant_room_url field
      callType,
      messageId: messageResponse.data.data.id,
      conversationId: props.conversationId,
      otherParticipant: props.otherParticipant,
      callId: roomData.call_id, // Include call_id in the event
      userRole: roomData.user_role || 'host' // Include user role (caller is usually host)
    });
    
    // Log successful call initiation
    console.log('✅ CALL INITIATED SUCCESSFULLY:', {
      timestamp: new Date().toISOString(),
      roomId: roomData.room_id,
      callId: roomData.call_id,
      callType: callType,
      conversationId: props.conversationId
    });
    
    // Show success message
    try {
      useAlert(`${callType === 'video' ? 'Video' : 'Voice'} call request sent`, 'success');
    } catch (alertError) {
      console.log(`${callType === 'video' ? 'Video' : 'Voice'} call request sent`);
    }
    
  } catch (error) {
    console.error('❌ ERROR INITIATING CALL:', {
      timestamp: new Date().toISOString(),
      error: error.message,
      response: error.response?.data,
      callType: callType,
      conversationId: props.conversationId
    });
    
    const errorMessage = error.response?.data?.message || error.message || 'Failed to initiate call';
    try {
      useAlert(errorMessage, 'danger');
    } catch (alertError) {
      console.error('Alert error:', alertError);
      console.log('Error:', errorMessage);
    }
    emit('error', errorMessage);
  } finally {
    loading.value = false;
  }
};

/**
 * Handle click outside to close dropdown
 */
const handleClickOutside = (event) => {
  const dropdown = event.target.closest('.tw-relative');
  if (!dropdown && showDropdown.value) {
    closeDropdown();
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>
